import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Info, X } from "lucide-react";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../../../components/ui/resizable";
import { fetchFastApi } from "../../../api/fastapi";
import { useAudit } from "../../../contexts/auditContext";
import { useAuth } from "../../../contexts/authContext";
import { useEvidenceReviewBannerDismissed } from "../../../hooks/useEvidenceReviewBannerDismissed";
import { useSessionRunsContext } from "../../../contexts/sessionRunsContext";
import { useHookCache } from "../../../lib/hookCache";
import { useControlSessionStatus } from "../../../hooks/audit/useControlSessionStatus";
import { useSessionRunsTransitions } from "../../../hooks/audit/useSessionRunsTransitions";
import { useSessionRunsPolling } from "../../../hooks/audit/useSessionRunsPolling";
import { ProcedureSessionTableShell } from "../../../models/audit/procedureSessionTableShell";
import { getOpenFoldOut } from "../auditWorkPaperViewV2/utils/foldOutSelection";
import { AuditWorkpaperLeftPanel } from "../auditWorkPaperViewV2/components/AuditWorkpaperLeftPanel";
import { ProcedureSessionWorkPaper } from "../auditWorkPaperViewV2/components/ProcedureSessionWorkPaper";
import { useSessionAnalyticsOwner } from "../../../hooks/audit/useSessionAnalyticsOwner";

// A queued generation takes up to ~60s to surface a real `processing` status,
// so the optimistic flip has to survive at least that long before it is treated
// as a run that never started.
const GENERATE_START_BACKSTOP_MS = 60_000;

/**
 * Thin shell for the work-paper view. The shell:
 * - owns control-session-wide polling (`useControlSessionStatus`,
 *   `useSessionRunsTransitions`) and the workpaper-data poller;
 * - exposes the whole-control-session `handleGenerateWorkPaper` action,
 *   which fans out across every enabled procedure session;
 * - renders the chrome via `AuditWorkpaperLeftPanel` (generate button,
 *   audit-session tabs, event-runs popover);
 * - renders N stacked `<ProcedureSessionWorkPaper>` instances inside a
 *   `flex flex-col gap-4` column — one per enabled procedure session,
 *   sorted by `procedureOrder`. Each child drives its own per-psId-keyed
 *   lazy fetches; the shared shell-scope `showRightPanel` / `sidePanelHost`
 *   / `currentSelection` drive a single right panel that follows whichever
 *   stacked table's cell the user clicked.
 *
 * Freshness is spread across three independent hooks rather than one merged
 * poller — `useControlSessionStatus` (5s tick), `useSessionRunsTransitions`
 * (queued→ready cascade) and `useSessionRunsPolling` (the workpaper-data
 * poller). Keeping them separate lets each key its invalidations to what it
 * observes, so one settling run does not refetch the whole view.
 */

export const AuditWorkpaperViewPageV2 = () => {
  const { controlSessionId } = useParams();
  const {
    controlSession,
    updateCurrentDocument,
    currentSelection,
    sessionKind,
  } = useAudit();
  const analyticsOwner = useSessionAnalyticsOwner();
  const { user } = useAuth();
  const [evidenceReviewBannerDismissed, dismissEvidenceReviewBanner] =
    useEvidenceReviewBannerDismissed(user?.id);
  const { setEntry, invalidate } = useHookCache();
  // The refetch burst covering the 202-async dispatch window lives in the
  // context (alongside the fetch it schedules) so the stop flow can cancel
  // it — see `burstFetchSessionRuns` / `cancelSessionRunsBurst` there.
  const { fetchSessionRuns, burstFetchSessionRuns } = useSessionRunsContext();

  // Invalidation cascades run at shell scope so they survive
  // procedure-session switches inside the per-procedure child.
  const statusQuery = useControlSessionStatus(
    controlSessionId,
    sessionKind,
    analyticsOwner,
  );
  useSessionRunsTransitions(controlSessionId, analyticsOwner);
  useSessionRunsPolling(controlSessionId, statusQuery.data);

  // Stable handle to the status refetch so the generate-bridge effect can poll
  // without re-subscribing every render (the query object identity changes each
  // tick).
  const statusRefetchRef = useRef(statusQuery.refetch);
  statusRefetchRef.current = statusQuery.refetch;

  // Instant "starting generation" feedback, bridging the gap between the click
  // and the polled status reflecting `processing` (see handleGenerateWorkPaper).
  const [isStarting, setIsStarting] = useState(false);
  // Side-panel state lives at shell scope so the right ResizablePanel can
  // sit alongside `AuditWorkpaperLeftPanel` as a full-viewport-height
  // sibling. `ProcedureSessionWorkPaper` owns all the side-panel-relevant
  // cell state and handlers — it renders `SupportColSidePanelV2` into
  // `sidePanelHost` via `createPortal` so we don't have to lift every handler.
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [sidePanelHost, setSidePanelHost] = useState<HTMLDivElement | null>(
    null,
  );
  const firstControlSessionFetch = useRef(true);

  // Enabled procedure-session ids (light — read off the envelope, no heavy
  // fetch). Drives both the stacked-render `.map()` below and the
  // `handleGenerateWorkPaper` fan-out across every enabled procedure session.
  const enabledProcedureSessionIds = useMemo<string[]>(() => {
    if (!controlSession) return [];
    return controlSession.procedureSessions
      .filter((ps) => ps.procedure.isEnabled)
      .sort(
        (a, b) =>
          (a.procedure.procedureOrder || 0) - (b.procedure.procedureOrder || 0),
      )
      .map((ps) => ps.id);
  }, [controlSession]);

  // Initial session-runs fetch once we know the control-session id. The
  // workpaper-data payload is now read lazily from the cache by
  // `ProcedureSessionWorkPaper` via `useWorkpaperData(psId)` — that hook
  // registers itself with `HookCacheProvider`, so the existing
  // `invalidate(["workpaper-data", psId])` call inside
  // `useSessionRunsTransitions` flips the cache entry and the subscribed
  // consumer re-renders. No shell-scope manual fetch is required.
  useEffect(() => {
    if (controlSessionId) {
      void fetchSessionRuns(controlSessionId);
    }
  }, [controlSessionId, fetchSessionRuns]);

  useEffect(() => {
    if (firstControlSessionFetch.current) {
      updateCurrentDocument(null);
      firstControlSessionFetch.current = false;
    }
  }, [controlSession, updateCurrentDocument]);

  // How the three hooks divide the work:
  //   - useControlSessionStatus ticks the lifecycle status endpoint and, on a
  //     per-field transition to "ready", invalidates only that narrow
  //     sub-key. Never the bare procedure-session key, which would sweep
  //     every lazily-keyed child entry under it at once.
  //   - useSessionRunsPolling refetches the session runs on the same cadence
  //     while any state is not ready, keeping the context warm — without it
  //     the flips below are never observed at all.
  //   - useSessionRunsTransitions turns each observed flip into the targeted
  //     invalidations documented at that hook, which is the one place their
  //     list is worth keeping.

  // Drop the optimistic `workPaperState` flip by re-fetching the affected
  // table-shell entries. Re-fetching rather than writing a literal is what
  // makes the retraction unconditionally safe: the pre-flip state may have been
  // "pending" rather than "ready", and by the time a retraction runs the server
  // may have moved on again. Non-existent entries are skipped, so this is a
  // no-op for procedure sessions that are not mounted.
  const resyncWorkPaperState = useCallback(() => {
    for (const psId of enabledProcedureSessionIds) {
      void invalidate(["procedure-session", psId, "table-shell"]);
    }
  }, [enabledProcedureSessionIds, invalidate]);

  // Stable handle so the generate-bridge effect can retract without listing the
  // callback as a dependency — an envelope refresh mid-generation would
  // otherwise re-run the effect and restart the backstop timer from zero.
  const resyncWorkPaperStateRef = useRef(resyncWorkPaperState);
  resyncWorkPaperStateRef.current = resyncWorkPaperState;

  // Whole-control-session generate: fan-out the optimistic
  // `workPaperState: 'processing'` flip across every enabled procedure
  // session's table-shell cache entry, then POST.
  const handleGenerateWorkPaper = useCallback(async () => {
    if (!controlSession) {
      toast.error("No audit session found");
      return;
    }

    // Immediate local feedback. The button's `isProcessing` is derived from the
    // 5s-polled status query, but generation is queued asynchronously: this POST
    // returns before `work_paper_state` flips to "processing" (that happens later,
    // when the background task actually starts the run). So we flip a local flag
    // now to show loading instantly; the bridge effect below keeps it true and
    // polls the status until "processing" appears, then hands off to the polled
    // `isProcessing`. We deliberately do NOT clear it here on success.
    setIsStarting(true);
    try {
      // Optimistic flip on the per-procedure **table-shell** cache entries
      // (the primary cache). With stacked rendering every
      // enabled procedure session has its own table-shell cache entry
      // mounted, so the flip lands on every active table.
      // `setEntry` no-ops on non-existent entries (stale entries are
      // re-fetched on next navigation).
      for (const psId of enabledProcedureSessionIds) {
        setEntry<ProcedureSessionTableShell>(
          ["procedure-session", psId, "table-shell"],
          (prev) => ({
            ...prev,
            procedureSession: {
              ...prev.procedureSession,
              workPaperState: "processing",
            },
          }),
        );
      }

      // Same shape either side, different router — `sessionKind` picks the
      // endpoint, as it does for the session envelope and the document
      // pipeline (`auditContext`). Calling the control path for a work-step
      // session used to answer 200 "generation began successfully" and then
      // never resolve the spinner, because the work never started.
      await fetchFastApi({
        path:
          sessionKind === "work_step"
            ? `/work-steps/work-step-session/${controlSessionId}/generate-work-paper`
            : `/controls/v2/control-session/${controlSessionId}/generate-work-paper`,
        method: "POST",
      });

      // The server owns the unlocked-cells computation; the FE only re-reads
      // the session runs it creates. That dispatch is async server-side, so a
      // burst of refetches rather than a single one covers the window before
      // those rows exist — see `burstFetchSessionRuns`.
      if (controlSessionId) {
        burstFetchSessionRuns(controlSessionId);
      }
    } catch (error) {
      console.error("Error generating workpaper:", error);
      toast.error("Error generating workpaper");
      // The request failed, so nothing is generating — drop the local flag and
      // let the server's state replace the optimistic one.
      setIsStarting(false);
      resyncWorkPaperState();
    }
  }, [
    controlSession,
    enabledProcedureSessionIds,
    setEntry,
    controlSessionId,
    burstFetchSessionRuns,
    sessionKind,
    resyncWorkPaperState,
  ]);

  // Bind to the polled status, not the envelope: `useControlSessionStatus`
  // keeps `["control-session", id, "status"]` fresh on a 5s tick, while the
  // envelope (`controlSession`) only re-fetches on invalidate (manual, or the
  // run-completion refresh in useControlSessionStatus /
  // useSessionRunsTransitions) and would silently leave the spinner stuck.
  const isProcessing = statusQuery.data?.procedureSessions.some(
    (ps) => ps.workPaperState === "processing",
  );

  // Bridge the click → polled-status gap. Generation is queued asynchronously:
  // the POST returns before `work_paper_state` flips to "processing", and the
  // status query only auto-polls while something isn't "ready" — so on its own
  // nothing would observe the eventual transition (the button would flicker:
  // loading on click, off when the handler resolves, on again at the next poll).
  // While `isStarting` is set we keep the button loading AND actively poll the
  // status; once "processing" appears we hand off to `isProcessing` and stop. A
  // timeout backstops a silent backend so the button can't spin forever.
  //
  // The backstop also expires the optimistic flip. Reaching it does not prove
  // the run never started: `isProcessing` is a 2s sample, so a generation that
  // starts and settles inside a sample gap reaches the backstop too. The
  // retraction is safe not because the run is known dead, but because it resyncs
  // from the server rather than writing a "not processing" literal — a run that
  // did start is reflected at its true state, not clobbered. Do not swap the
  // resync for a literal cache write on the strength of "the run never started".
  // When real `processing` does arrive the branch above tears the timer down
  // first, so a live run is never retracted out from under itself.
  useEffect(() => {
    if (!isStarting) return;
    if (isProcessing) {
      setIsStarting(false);
      return;
    }
    const poll = setInterval(() => void statusRefetchRef.current(), 2000);
    const backstop = setTimeout(() => {
      setIsStarting(false);
      resyncWorkPaperStateRef.current();
    }, GENERATE_START_BACKSTOP_MS);
    return () => {
      clearInterval(poll);
      clearTimeout(backstop);
    };
  }, [isStarting, isProcessing]);

  if (!controlSession) {
    return (
      <div className="w-full flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  // When a procedure's cell fold-out is open, hide the other procedures so the
  // fold-out fills the page even when several procedures are stacked. The
  // hidden ones stay mounted, preserving their state. `getOpenFoldOut` (shared
  // with `ProcedureSessionWorkPaper`) is what decides a fold-out is really
  // open — a selection alone isn't enough: with the auditor-trail flag off, or
  // for cells that open no fold-out (doc-less support cells, check-result
  // sheets), the stacked tables must stay visible.
  const foldOutPsId = getOpenFoldOut(
    controlSession.auditorTrailEnabled === true,
    currentSelection,
    showRightPanel,
  )
    ? currentSelection?.procedureSessionId
    : undefined;

  return (
    <ResizablePanelGroup
      autoSaveId="conditional"
      direction="horizontal"
      className="h-screen w-full max-w-[2500px] mx-auto"
    >
      <ResizablePanel id="left-panel" order={1} defaultSize={100} minSize={30}>
        <AuditWorkpaperLeftPanel
          controlSession={controlSession}
          isProcessing={!!isProcessing || isStarting}
          onGenerateWorkPaper={handleGenerateWorkPaper}
          focusMode={!!foldOutPsId}
        >
          <div className="flex flex-col gap-4 h-full overflow-y-auto">
            {/* Flag-on sessions whose results predate the auditor-trail flag
                render the legacy walkthrough (auditorTrailEnabled is false), so
                without this banner users who just got the flag see no change
                and assume the feature is broken. Hidden while a generation run
                is in flight — "until the workpaper is regenerated" would
                contradict the visible run; once tests land, the envelope
                refresh (useControlSessionStatus / useSessionRunsTransitions)
                clears auditorTrailPending. The X dismisses it once per user,
                permanently (localStorage) — it never hides it for anyone else
                in the workspace. */}
            {controlSession.auditorTrailPending &&
              !evidenceReviewBannerDismissed &&
              !isProcessing &&
              !isStarting && (
                <Alert className="border-blue-200 bg-blue-50 text-blue-900 [&>svg]:text-blue-600">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="pr-8">
                    The new evidence review is enabled in this workspace but
                    won't appear for this control until the workpaper is
                    regenerated.
                  </AlertDescription>
                  <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={dismissEvidenceReviewBanner}
                    className="absolute right-2 top-1/2 -translate-y-1/2 !p-1 text-blue-600 hover:text-blue-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </Alert>
              )}
            {enabledProcedureSessionIds.map((psId) => {
              const isActive = !foldOutPsId || psId === foldOutPsId;
              return (
                <div key={psId} className={!isActive ? "hidden" : ""}>
                  <ProcedureSessionWorkPaper
                    procedureSessionId={psId}
                    showRightPanel={showRightPanel}
                    setShowRightPanel={setShowRightPanel}
                    sidePanelHost={sidePanelHost}
                  />
                </div>
              );
            })}
          </div>
        </AuditWorkpaperLeftPanel>
      </ResizablePanel>
      {showRightPanel && (
        <>
          <ResizableHandle
            withHandle
            data-testid="workpaper-review-panel-resize-handle"
          />
          <ResizablePanel
            id="right-panel"
            order={2}
            defaultSize={40}
            minSize={20}
          >
            <div ref={setSidePanelHost} className="h-full" />
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );
};
